import { create } from 'zustand';
import { API_BASE } from '../config';
import { useCityStore } from './cityStore';
import { useCutscene } from './cutscene';
import { stateAt, geoReady, setGeo } from './cityMotion';
import { planShot, chooseSide } from './sceneShots';
import { useWorldStore } from '../state/worldStore';

// REPLAY ON RAILS — an episode of a City Story played back in the city, as it
// would be seen (Khari, 2026-10-24, on the review tab: "there is nothing to
// view how this would look").
//
//   world.hypetunes.com/?story=<id>&rt=<pass>
//
// The episode (API GET /api/world/city/stories/:id/replay) is staged on the
// city's clock from NOW: the lead walking or driving between the scenes'
// places on the episode's own timing, co-stars arriving for theirs, every line
// at its offset, the song under it — a city of just its cast. The live city is
// set aside while it plays (cityStore `replay`), and the cutscene director
// films it like a live episode; between scenes the camera rides behind the
// lead (view-only). The pass comes from the review tab, so an episode that has
// not aired yet opens without anyone's login in a URL; an aired one needs none.

const qs = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
export const REPLAY_ID = qs && /^\d+$/.test(qs.get('story') || '') ? Number(qs.get('story')) : null;
const REPLAY_PASS = qs ? qs.get('rt') : null;
// The first scene's first line comes a beat after it is staged; the beat is
// already the scene (its establishing view, out of the fly-in's landing —
// replayLanding), never a wait.
const LEAD_MS = 1000;
const AROUND_MS = 10 * 60000;

export const useReplay = create(() => ({
  state: REPLAY_ID ? 'loading' : 'off',   // off | loading | ready | playing | done | error
  error: null,
  episode: null,                           // the API's episode
  places: null,
  key: null,                               // its id in the city store
  t1: 0,
}));

export async function loadReplay() {
  if (!REPLAY_ID) return;
  try {
    const res = await fetch(`${API_BASE}/api/world/city/stories/${REPLAY_ID}/replay${REPLAY_PASS ? `?rt=${encodeURIComponent(REPLAY_PASS)}` : ''}`, { cache: 'no-store' });
    const d = await res.json().catch(() => ({}));
    if (!res.ok || !d.success) throw new Error(d.error || `Could not load the episode (${res.status})`);
    useReplay.setState({ state: 'ready', episode: d.story, places: d.places || null, error: null });
  } catch (err) {
    useReplay.setState({ state: 'error', error: err.message });
  }
}

// THE EDIT (Khari, 2026-10-24, watching one: "the camera starts on the character
// talking, then it pauses for a long time as they just stand there.. After a
// long time, the character got into a car… At the end of the conversation, the
// character just stands there waiting for the song to end… We shouldn't have
// any long pauses in the story"). An episode was booked in the city at the
// song's length, its scenes spread across it, so a scene of three short lines
// held a minute and the trip waited for its slot. A replay plays an EDIT of it:
//   · a scene is its establishing beat, its lines (a breath between them, never
//     a silence) and a beat to finish;
//   · a trip takes what it takes at a natural pace, and a long one is CUT —
//     setting off, then arriving: two parts of the same walk or drive
//     (`pr`, `cut` — city/cityMotion);
//   · it ends on its last line, the song fading out under the last beat.
// Episodes written from now on are already cut to their content (API
// CityStoryService.tighten); a tight one passes through here unchanged.
const LEAD_IN_MS = 2500;      // a scene's establishing view, before its first line
const BREATH_MS = 650;        // the longest pause kept between two lines
const SCENE_TAIL_MS = 1500;   // a beat after a scene's last line, before they move on
const END_TAIL_MS = 3200;     // …and after the episode's last: the song fades out over it
const EMPTY_SCENE_MS = 4500;  // a scene left with no lines: seen, not dwelt on
const SAME_PLACE_MS = 800;
const WALK_SPEED = 1.0;       // units/s, unhurried (the walk clip covers 0.9 at rate 1)
const CAR_SPEED = 9;          // units/s along the streets (the traffic drives 6.5–9)
const STREETS = 1.3;          // a way through the streets is about this much longer than the straight line
const KERB_MS = 7000;         // a drive's walks to the car and from it, together
const WALK_MAX_MS = 9000;     // a longer walk is cut…
const DRIVE_MAX_MS = 12000;   // …and a longer time in the car
const SET_OFF_MS = 4500;      // shown of a cut trip before the cut…
const ARRIVE_MS = 5000;       // …and after it

/** A trip between two places as the edit shows it: its parts, and how long they take together. */
function tripFor(from, to, travel, placeAt) {
  if (from === to) return { ms: SAME_PLACE_MS, parts: [] };
  const A = placeAt(from); const B = placeAt(to);
  const d = (A && B ? Math.hypot(A[0] - B[0], A[1] - B[1]) : 40) * STREETS;
  if (travel === 'drive') {
    const carMs = (d / CAR_SPEED) * 1000;
    const full = Math.round(KERB_MS + carMs);
    if (carMs <= DRIVE_MAX_MS) return { k: 'drive', ms: full, parts: [{ ms: full }] };
    const a = KERB_MS / 2 + SET_OFF_MS; const b = KERB_MS / 2 + ARRIVE_MS;
    return { k: 'drive', ms: a + b, parts: [{ ms: a, pr: [0, a / full], full }, { ms: b, pr: [1 - b / full, 1], full, cut: true }] };
  }
  const walkMs = Math.max(3000, (d / WALK_SPEED) * 1000);
  if (walkMs <= WALK_MAX_MS) return { k: 'walk', ms: walkMs, parts: [{ ms: walkMs }] };
  return { k: 'walk', ms: SET_OFF_MS + ARRIVE_MS, parts: [{ ms: SET_OFF_MS, pr: [0, SET_OFF_MS / walkMs] }, { ms: ARRIVE_MS, pr: [1 - ARRIVE_MS / walkMs, 1], cut: true }] };
}

/**
 * The edit of an episode, in ms from its first scene: scenes [{ ...scene,
 * startMs, endMs }] with the trip into each (`trip`), lines [{ ...line, atMs }],
 * and its length. Pure.
 */
export function cutDown(ep, places) {
  const placeAt = (slug) => (places?.places || []).find((p) => p.slug === slug)?.at || null;
  const scenes = (ep.scenes || []).filter((s) => s.startMs != null && s.endMs != null).sort((a, b) => a.startMs - b.startMs);
  const byScene = new Map();
  for (const l of ep.lines || []) {
    if (l.atMs == null) continue;
    if (!byScene.has(l.scene)) byScene.set(l.scene, []);
    byScene.get(l.scene).push(l);
  }
  let t = 0;
  const outScenes = []; const outLines = [];
  scenes.forEach((s, n) => {
    let trip = null;
    if (n > 0) { trip = tripFor(scenes[n - 1].place, s.place, s.travel, placeAt); t += trip.ms; }
    const t0 = t;
    const mine = (byScene.get(s.i) || []).slice().sort((a, b) => a.atMs - b.atMs);
    let at = t0 + LEAD_IN_MS; let end = null; let wasEnd = null;
    for (const l of mine) {
      const dur = l.durMs || 3000;
      if (end != null) at = end + Math.max(250, Math.min(BREATH_MS, l.atMs - wasEnd));
      outLines.push({ ...l, atMs: at });
      end = at + dur; wasEnd = l.atMs + dur;
    }
    const last = n === scenes.length - 1;
    const t1 = end != null ? end + (last ? END_TAIL_MS : SCENE_TAIL_MS) : t0 + EMPTY_SCENE_MS;
    outScenes.push({ ...s, startMs: t0, endMs: t1, trip });
    t = t1;
  });
  return { scenes: outScenes, lines: outLines, durMs: t };
}

/**
 * Stage the episode from `now` (server clock) and step into it. The cast's
 * timelines in the city's own segment shape (API cityTimeline compactSeg):
 * stays at the scenes' spots, walks and drives between places — as edited
 * (cutDown above).
 */
export function playReplay(now) {
  const { episode: ep, places } = useReplay.getState();
  if (!ep) return;
  const cut = cutDown(ep, places);
  const scenes = cut.scenes;
  if (!scenes.length) { useReplay.setState({ state: 'error', error: 'This episode has no scenes to play' }); return; }
  const base = now + LEAD_MS;                    // edit ms 0 → this server time
  const at = (ms) => base + ms;
  const key = `replay-${ep.id}`;
  const pairs = places?.pairs || [[0, 1], [2, 3]];
  const inScene = (s) => !!(s.with && s.with.said === 'yes');
  // INSIDE (2026-10-29): an indoor scene stands on the room's marks (`in0` the
  // lead, `in1` a co-star) with `r` = the room, exactly as live: RoomStory draws
  // them, StoryRoomSwitch takes the viewer in, RoomStoryAudio plays it.
  const spotOf = (s) => (s.room ? 'in0' : inScene(s) ? pairs[s.pair || 0][0] : 4);
  const inRoom = (s) => (s.room ? { r: s.room } : {});

  // THE LEAD: there before the first scene, scene to scene, still there after.
  const lead = [];
  let prev = null;
  for (const s of scenes) {
    const t0 = at(s.startMs); const t1 = at(s.endMs);
    const spot = spotOf(s);
    if (!prev) {
      // Already in the first scene's place, and counted as that scene (no chase before it).
      lead.push({ k: 'stay', t0: t0 - AROUND_MS, t1: t0, m: 'story', p: s.place, a: s.what || ep.title, st: key, sc: s.i, s: spot, ...inRoom(s) });
    } else if (!s.trip?.parts.length) {
      lead.push({ k: 'stay', t0: prev.t1, t1: t0, m: 'story', p: s.place, st: key, sc: -1, s: spot, ...inRoom(s) });
    } else {
      let c = prev.t1;
      for (const part of s.trip.parts) {
        lead.push({
          k: s.trip.k, t0: c, t1: c + part.ms, m: 'story', f: { place: prev.s.place }, to: { place: s.place }, fs: prev.spot, ts: spot, st: key,
          ...(part.pr ? { pr: part.pr } : {}), ...(part.full ? { full: part.full } : {}), ...(part.cut ? { cut: true } : {}),
        });
        c += part.ms;
      }
    }
    lead.push({ k: 'stay', t0, t1, m: 'story', p: s.place, a: s.what || ep.title, st: key, sc: s.i, s: spot, ...inRoom(s) });
    prev = { s, t1, spot };
  }
  lead.push({ k: 'stay', t0: prev.t1, t1: prev.t1 + AROUND_MS, m: 'story', p: prev.s.place, st: key, sc: -1, s: prev.spot });

  // THE CO-STARS: there for their scenes (a little before), nowhere else.
  const cast = new Map();   // avatarId → { name, segs }
  for (const s of scenes) {
    if (!inScene(s)) continue;
    const id = Number(s.with.id);
    const c = cast.get(id) || { name: s.with.name, segs: [] };
    const t0 = at(s.startMs); const t1 = at(s.endMs);
    const from = Math.max(t0 - 30000, c.segs.length ? c.segs[c.segs.length - 1].t1 : -Infinity);
    c.segs.push({ k: 'stay', t0: from, t1, m: 'story', p: s.place, a: `in ${ep.name}'s story`, st: key, sc: s.i, s: s.room ? 'in1' : pairs[s.pair || 0][1], ...inRoom(s) });
    cast.set(id, c);
  }

  const story = {
    id: key, a: ep.avatarId, name: ep.name, chapter: ep.chapter, ep: ep.ep, series: ep.series, title: ep.title,
    t0: base, t1: base + cut.durMs,
    scenes: scenes.map((s) => ({ i: s.i, place: s.place, t0: at(s.startMs), t1: at(s.endMs), w: inScene(s) ? Number(s.with.id) : null, r: s.room || null })),
    lines: cut.lines.map((l) => ({ idx: l.idx, scene: l.scene, speaker: l.speaker, text: l.text, playAt: at(l.atMs), dur: (l.durMs || 3000) / 1000, audio: l.audio || null })),
    score: ep.score?.url ? { title: ep.score.title, durMs: ep.score.durMs, url: ep.score.url } : null,
  };
  const snap = {
    v: 1, t: now, dateStr: ep.dateStr, tz: 'America/Los_Angeles', ...(places || {}),
    residents: [{ avatarId: ep.avatarId, name: ep.name, segs: lead }, ...[...cast.entries()].map(([avatarId, c]) => ({ avatarId, name: c.name, segs: c.segs }))],
    convs: [], holds: [], stories: [story], shows: [], sign: null,
  };
  useCityStore.getState().setSnap(snap, { replay: true });
  // The motion reads its places now, not when the residents next re-render.
  setGeo(snap, useWorldStore.getState().venueAnchors || null);
  useReplay.setState({ state: 'playing', key, t1: story.t1 });
  // At once: the camera is waiting on the scene's opening frame (replayLanding).
  const title = `${ep.name} · ${ep.chapter === 'morning' ? 'This morning' : ep.chapter === 'midday' ? 'Midday' : 'Tonight'} · Episode ${ep.ep}: ${ep.title}`;
  useCutscene.getState().stepIn({ kind: 'story', id: key, title });
}

/**
 * WHERE THE FLY-IN LANDS (Khari, 2026-10-24: "When we fly in, we should fly
 * into the character's position and start the scene from there."). Arriving in
 * the city is a drone descent onto the street (PlanetScene); for a replay it
 * comes down onto the episode's first scene instead — circling the lead's spot
 * and settling exactly where that scene's establishing shot begins, worked out
 * the way the cutscene director will work it out (same positions, same plan),
 * so the episode starts out of the descent without a cut. { pos, look }, or
 * null while there is nothing to land on yet (the city's places not in).
 */
export function replayLanding(aspect = 16 / 9) {
  const { episode: ep, places } = useReplay.getState();
  const s = ep && cutDown(ep, places).scenes[0];
  if (!s) return null;
  // The descent can start before the live city has sent its snapshot: the
  // episode carries the same places, and the venue anchors are already in
  // (the live snapshot replaces this the moment it arrives — CityResidents).
  if (!geoReady() && places?.places?.length) setGeo({ ...places }, useWorldStore.getState().venueAnchors || null);
  const pairs = places?.pairs || [[0, 1], [2, 3]];
  const duet = !!(s.with && s.with.said === 'yes');
  const heights = new Map((useWorldStore.getState().npcList || []).map((n) => [Number(n.avatarId), n.height || 0.75]));
  const person = (id, spot) => {
    const st = stateAt({ avatarId: id, segs: [{ k: 'stay', t0: 0, t1: 2, m: 'story', p: s.place, s: spot, st: 'landing', sc: s.i }] }, 1, null);
    if (!st || (!st.x && !st.z)) return null;
    return { id, x: st.x, z: st.z, facing: st.heading || 0, head: (st.y || 0) + (heights.get(Number(id)) || 0.75) * 0.92 };
  };
  const S = person(ep.avatarId, duet ? pairs[s.pair || 0][0] : 4);
  if (!S) return null;
  const L = duet ? person(Number(s.with.id), pairs[s.pair || 0][1]) : null;
  const plan = planShot('establish', S, L, L ? chooseSide(S, L) : 1, aspect, 1);
  return { pos: plan.at(0), look: plan.look0 };
}

/** Loaded and waiting for the arrival to start it. */
export const replayPending = () => useReplay.getState().state === 'ready';

/** The episode has played out (the cutscene director calls this). */
export function replayDone() {
  if (useReplay.getState().state === 'playing') useReplay.setState({ state: 'done' });
}

if (import.meta.env.DEV && typeof window !== 'undefined') window.__replay = useReplay;   // probes only
